import axios from 'api/helpers/axios';
import type { AxiosResponse, AxiosError } from 'axios';

export interface ICity {
  id: number;
  name: string;
  subdomain: string;
  name_rod: string;
  name_pred: string;
  phone: string;
  email: string;
  address: string;
  is_active?: boolean;
  sort?: number;
  created_at?: string;
  updated_at?: string;
}

type Resp<T> = Promise<AxiosResponse<T>>;

export type IdType = number | string;

export interface RemoveOptions {
  /** Принудительное удаление, если бек такое поддерживает (?force=1) */
  force?: boolean;
  /** Возможность отменить запрос через AbortController */
  signal?: AbortSignal;
}

export interface SafeResultOk {
  ok: true;
}

export interface SafeResultErr {
  ok: false;
  status?: number;
  message: string;
}

export type SafeResult = SafeResultOk | SafeResultErr;

export interface BulkRemoveResult {
  ok: IdType[];
  failed: Array<{ id: IdType; status?: number; message: string }>;
}

const citiesApi = {
  getList(): Resp<ICity[]> {
    return axios.get<ICity[]>('/cities');
  },

  getOne(id: IdType): Resp<ICity> {
    return axios.get<ICity>(`/cities/${id}`);
  },

  create(data: Partial<ICity>): Resp<ICity> {
    return axios.post<ICity>('/cities', data);
  },

  update(id: IdType, data: Partial<ICity>): Resp<ICity> {
    return axios.put<ICity>(`/cities/${id}`, data);
  },

  /**
   * Базовый DELETE — возвращаем полный AxiosResponse<void>,
   * чтобы при необходимости можно было прочитать статус/заголовки.
   */
  remove(id: IdType, opts?: RemoveOptions): Resp<void> {
    return axios.delete<void>(`/cities/${id}`, {
      params: opts?.force ? { force: 1 } : undefined,
      signal: opts?.signal
    });
  },

  /**
   * Удобная обёртка без исключений: true/false + сообщение об ошибке.
   */
  async removeSafe(id: IdType, opts?: RemoveOptions): Promise<SafeResult> {
    try {
      await citiesApi.remove(id, opts);
      return { ok: true };
    } catch (err) {
      const e = err as AxiosError<any>;
      return {
        ok: false,
        status: e.response?.status,
        message:
          e.response?.data?.message || e.message || 'Не удалось удалить город'
      };
    }
  },

  /**
   * Пакетное удаление – удаляем все ids параллельно,
   * собираем успешные и неуспешные.
   */
  async removeMany(
    ids: IdType[],
    opts?: RemoveOptions
  ): Promise<BulkRemoveResult> {
    const results = await Promise.allSettled(
      ids.map((id) => citiesApi.remove(id, opts))
    );
    const ok: IdType[] = [];
    const failed: BulkRemoveResult['failed'] = [];

    results.forEach((res, idx) => {
      const id = ids[idx];
      if (res.status === 'fulfilled') {
        ok.push(id);
      } else {
        const e = res.reason as AxiosError<any>;
        failed.push({
          id,
          status: e.response?.status,
          message:
            e.response?.data?.message || e.message || 'Не удалось удалить'
        });
      }
    });

    return { ok, failed };
  }
};

export default citiesApi;
